import { Type } from "@themes/pollinator/ui/Type";
import { StepActions } from "@themes/pollinator/ui/StepActions";

export type Props = {
  /** Sub-step title */
  title: string;
  /** Optional id for the title element (for aria-labelledby association) */
  titleId?: string;
  /** Sub-step description */
  description?: string;
  /** Current step index (0-indexed). When omitted, progress indicator is hidden. */
  stepIndex?: number;
  /** Total number of steps. When omitted, progress indicator is hidden. */
  totalSteps?: number;
  /** Main content */
  children: React.ReactNode;
  /** Primary button text */
  primaryButtonText?: string;
  /** Whether primary button is disabled */
  isPrimaryDisabled?: boolean;
  /** Handler for primary button */
  onPrimaryClick: () => void;
  /** Handler for back button (hidden if stepIndex is 0 or undefined) */
  onBack?: () => void;
  /** Handler for skip button (hidden if undefined) */
  onSkip?: () => void;
};

/**
 * Shared layout for onboarding steps and sub-steps.
 * Provides consistent header and action bar at the bottom (matching quiz style).
 *
 * When stepIndex/totalSteps are provided, a progress indicator is shown.
 * When omitted, the progress indicator is hidden (for standalone steps).
 */
export function SubStepLayout({
  title,
  titleId,
  description,
  stepIndex,
  totalSteps,
  children,
  primaryButtonText = "Continue",
  isPrimaryDisabled = false,
  onPrimaryClick,
  onBack,
  onSkip,
}: Props) {
  return (
    <div className="flex-1 flex flex-col justify-between">
      {/* Content area */}
      <div className="w-full flex flex-col gap-12 p-6 max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <Type variant="h2" id={titleId}>{title}</Type>
          {description && (
            <Type variant="body1" className="text-brand-700">
              {description}
            </Type>
          )}
        </div>

        {/* Content */}
        <div className="flex-1">{children}</div>
      </div>

      {/* Action bar at bottom */}
      <StepActions
        stepIndex={stepIndex ?? 0}
        totalSteps={totalSteps ?? 0}
        showProgress={stepIndex !== undefined && totalSteps !== undefined}
        primaryLabel={primaryButtonText}
        onPrimaryClick={onPrimaryClick}
        isPrimaryDisabled={isPrimaryDisabled}
        onBack={onBack}
        onSkip={onSkip}
        progressPrefix="Step"
      />
    </div>
  );
}

export default SubStepLayout;
